import type { Directive } from "vue";
import router from "@/router";
import {
  ActivityCategory,
  trackActivity,
} from "@/services/activity/portalActivityTracker";

const TRACKED_ATTRIBUTE = "data-portal-track";
const SELECTION_CONTROL = ".v-switch, .v-checkbox, .v-checkbox-btn, .v-radio, .v-selection-control";
// The chips Vuetify answers Enter and Space on from keydown, with no click: clickable,
// and not a link, whose Enter the browser turns into a click.
const KEYBOARD_CHIP = ".v-chip.v-chip--link:not(a)";

type TrackedElement = HTMLElement & {
  __portalActivityName?: string;
  __portalActivityListener?: (event: Event) => void;
};

// v-track="'payments.submit'" records a click under a stable name. Names follow
// page.control so the Portal Usage figures survive a label being reworded.
const track: Directive<TrackedElement, string> = {
  mounted(element, binding) {
    element.__portalActivityName = binding.value;
    element.setAttribute(TRACKED_ATTRIBUTE, "");

    element.__portalActivityListener = (event: Event) => {
      if (event.type === "keydown") {
        const { key, repeat } = event as KeyboardEvent;
        // a held key is one choice, not one per repeat
        if (repeat || (key !== "Enter" && key !== " ") || !element.matches(KEYBOARD_CHIP)) {
          return;
        }
      }

      // A tracked button inside a tracked row is one click on the button, not two,
      // so only the innermost tracked element under the pointer records it.
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest(`[${TRACKED_ATTRIBUTE}]`) !== element) {
        return;
      }

      // A switch, checkbox or radio passes a click on its label or track on to its input,
      // so one toggle arrives twice; only the click on the input is counted. A tracked row
      // that merely holds a checkbox is not one, and counts its clicks as before.
      const toggle = element.matches(SELECTION_CONTROL)
        ? element.querySelector('input[type="checkbox"], input[type="radio"]')
        : null;
      if (toggle && target !== toggle) {
        return;
      }

      trackActivity(
        ActivityCategory.click,
        element.__portalActivityName ?? "",
        String(router.currentRoute.value.name ?? "")
      );
    };

    // Capture phase, so a control that stops propagation is still counted.
    element.addEventListener("click", element.__portalActivityListener, true);
    if (element.matches(".v-chip")) {
      element.addEventListener("keydown", element.__portalActivityListener, true);
    }
  },
  updated(element, binding) {
    element.__portalActivityName = binding.value;
  },
  beforeUnmount(element) {
    if (element.__portalActivityListener) {
      element.removeEventListener("click", element.__portalActivityListener, true);
      element.removeEventListener("keydown", element.__portalActivityListener, true);
    }
  },
};

export default track;
